import { FilterList } from '@mui/icons-material';
import { Box, Button, Divider, Stack } from '@mui/material';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  gridClasses,
  type GridRenderCellParams,
  type GridRowParams,
} from '@mui/x-data-grid-pro';
import { startOfToday } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { url } from '../../appRouter/url';
import { FilterDrawer } from '../../components/filter/FilterDrawer';
import {
  akaVeranstaltungIdColumn,
  EasyBreadcrumbs,
  type EasyBreadcrumbsItem,
  veranstaltungSapIdColumn,
} from '../../components/general';
import { DataGridPanelToggle } from '../../components/general/DataGridPanelToggle';
import { FailedQueryAlert } from '../../components/general/ErrorAlert';
import { Legend } from '../../components/general/Legend';
import { PageTitle } from '../../components/general/PageTitle';
import { FilterDrawerAccordion } from '../../components/general/ResourceCalendar/Filters/FilterDrawerAccordion';
import { type FachlicherFehler } from '../../dtos';
import { usePageTitle } from '../../hooks/usePageTitle';
import { trpc } from '../../trpc';
import { type DataGridColDef, localStorageKeys, printWithNoInfo } from '../../utils';
import { formatDate, formatTimestamp } from '../../utils/dateFormat/dateFormat';
import { getDetailPanelHeight } from '../../utils/getDetailPanelHeight.ts';
import { BeginFilter } from './BeginFilter';
import { getLegendElements, renderGeschaeftsbereich, renderLevel } from './FehlerPage.utils';
import { FehlerPageDetailPanel } from './FehlerPageDetailPanel';
import { FehlerTypFilter, fehlerTypFilterKey, useFehlerTypFilter } from './FehlerTypFilter';
import { FilterChips } from './FilterChips';
import {
  GeschaeftsbereichFilter,
  geschaeftsbereichFilterKey,
  useGeschaeftsbereichFilter,
} from './GeschaeftsbereichFilter';
import { beginFilterKeys, filterOptions, useBeginFilterParams } from './hooks/useBeginFilterParams';
import { useInitialFehlerPageParams } from './hooks/useInitialFehlerPageParams';
import { LevelFilter, levelFilterKey, useLevelFilter } from './LevelFilter';

const columns: DataGridColDef<FachlicherFehler> = [
  {
    field: GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
    type: 'string',
    headerName: '',
    width: GRID_DETAIL_PANEL_TOGGLE_COL_DEF.width,
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: GRID_DETAIL_PANEL_TOGGLE_COL_DEF.valueGetter,
    renderCell: (params: GridRenderCellParams<FachlicherFehler>) => <DataGridPanelToggle {...params} />,
  },
  {
    field: 'level',
    type: 'string',
    headerName: '',
    flex: 0.08,
    resizable: false,
    disableColumnMenu: true,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    renderCell: (params) => renderLevel(params.value),
  },
  { field: 'code', headerName: 'Fehler-Typ', flex: 0.6, valueGetter: (_value, row) => printWithNoInfo(row.code) },
  {
    field: 'beginnDesFehlerhaftenDatensatzes',
    type: 'date',
    headerName: 'Veranstaltungsbeginn',
    flex: 0.6,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    renderCell: (params) => formatDate(params.value, true),
    description:
      'Die Spalte enthält in der Regel den Veranstaltungsbeginn. Bei Experten- und Raumblockungsfehlern wird hier das Datum der Blockung angezeigt.',
  },
  akaVeranstaltungIdColumn(),
  veranstaltungSapIdColumn(),
  {
    field: 'geschaeftsbereich',
    headerName: 'Geschäftsbereich',
    flex: 0.5,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    renderCell: (params) => renderGeschaeftsbereich(params.value),
  },
  {
    field: 'createdAt',
    type: 'dateTime',
    headerName: 'Zuletzt aufgetreten',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    renderCell: (params) => formatTimestamp(params.value),
    flex: 0.5,
  },
];

const getFilterCount = (searchParams: URLSearchParams): string | null => {
  let count = 0;
  for (const key of searchParams.keys()) {
    if ([beginFilterKeys.option, geschaeftsbereichFilterKey, levelFilterKey, fehlerTypFilterKey].includes(key)) {
      count++;
    }
  }

  return count > 0 ? `(${count})` : null;
};

export const FehlerPage = () => {
  usePageTitle('Fehler & Warnungen');
  useInitialFehlerPageParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [{ startDate, endDate }, { setOption }] = useBeginFilterParams();
  const [searchParams] = useSearchParams();
  const [levelFilter, setLevelFilter] = useLevelFilter();
  const [geschaeftsbereichFilter, setGeschaeftsbereichFilter] = useGeschaeftsbereichFilter();
  const [fehlerTypFilter, setFehlerTypFilter] = useFehlerTypFilter();

  // Store search params in local storage
  useEffect(() => {
    const storedSearchParams = localStorage.getItem(localStorageKeys.FEHLER_PAGE_SEARCH_PARAMS);
    if (storedSearchParams !== searchParams.toString()) {
      localStorage.setItem(localStorageKeys.FEHLER_PAGE_SEARCH_PARAMS, searchParams.toString());
    }
  }, [searchParams]);

  const fehlerResult = trpc.fehler.getFachlicheFehler.useQuery({
    start: startDate ?? startOfToday(),
    end: endDate,
    level: levelFilter,
    geschaeftsbereiche: geschaeftsbereichFilter,
    typs: fehlerTypFilter,
  });

  const breadcrumbs: EasyBreadcrumbsItem[] = [
    {
      href: url.root(),
      label: 'Dashboard',
    },
    {
      href: '',
      label: 'Fehler & Warnungen',
    },
  ];

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<FachlicherFehler>) => (
      <FehlerPageDetailPanel titel={row.veranstaltungTitel} message={row.message} />
    ), //
    [],
  );

  const resetAllFilters = (): void => {
    setOption(filterOptions.WITHIN_4_WEEKS);
    setLevelFilter(['error']);
    setGeschaeftsbereichFilter([]);
    setFehlerTypFilter([]);
  };

  if (fehlerResult.error) {
    return <FailedQueryAlert error={fehlerResult.error} query="Fehler" />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingY: 2,
        boxSizing: 'border-box',
      }}
    >
      <EasyBreadcrumbs items={breadcrumbs} />
      <PageTitle title="Fehler & Warnungen" reloadQuery={fehlerResult} />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
          <LevelFilter />
          <BeginFilter />
          <GeschaeftsbereichFilter />
          <Button onClick={() => setDrawerOpen(true)} variant="text" size="small">
            <FilterList sx={{ mr: 1 }} /> Alle Filter {getFilterCount(searchParams)}
          </Button>
        </Box>
        <Legend elements={getLegendElements()} />
      </Stack>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FilterChips />
        <Button onClick={resetAllFilters} variant="text" size="small">
          Alle zurücksetzen
        </Button>
      </Box>
      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ height: 'calc(100vh - 204px)', overflowY: 'auto' }}>
          <Stack spacing={1} display="flex" divider={<Divider flexItem />} sx={{ height: '100%', overflowY: 'auto' }}>
            <FilterDrawerAccordion title="Level" expanded={levelFilter.length > 0}>
              <LevelFilter inDrawer />
            </FilterDrawerAccordion>
            <FilterDrawerAccordion title="Veranstaltungsbeginn" expanded>
              <BeginFilter inDrawer />
            </FilterDrawerAccordion>
            <FilterDrawerAccordion title="Geschäftsbereiche" expanded={geschaeftsbereichFilter.length > 0}>
              <GeschaeftsbereichFilter inDrawer />
            </FilterDrawerAccordion>
            <FilterDrawerAccordion title="Fehler-Typ" expanded={fehlerTypFilter.length > 0}>
              <FehlerTypFilter key={`FehlerTypFilter_${searchParams}`} inDrawer />
            </FilterDrawerAccordion>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} mt={1}>
          <Button
            onClick={resetAllFilters}
            variant="outlined"
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', pt: 1 }}
          >
            Alle zurücksetzen
          </Button>

          <Button onClick={() => setDrawerOpen(false)} variant="contained" size="small" sx={{ flex: 1 }}>
            Anwenden
          </Button>
        </Stack>
      </FilterDrawer>

      <DataGridPro
        columns={columns}
        rows={fehlerResult.data ?? []}
        getRowId={(fehler) => fehler.id}
        getRowHeight={() => 'auto'}
        initialState={{
          sorting: {
            sortModel: [
              { field: 'level', sort: 'asc' },
              { field: 'beginnDesFehlerhaftenDatensatzes', sort: 'asc' },
            ],
          },
        }}
        sx={{
          height: '100%',
          width: '100%',
          [`& .${gridClasses.cell}`]: {
            py: 1,
          },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
          },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
          },
        }}
        disableRowSelectionOnClick
        loading={fehlerResult.isLoading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
      />
    </Box>
  );
};
